import React, { Fragment, useState } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

import UserMenu from "../UserMenu";
import SideMenuItem from "./SideMenuItem";
import TrialSidebarBanner from "../TrialSidebarBanner";
import SideMenuToggleButton from "./SideMenuToggleButton";
import { sideMenuBottomItems, sideMenuMainItems } from "./SideMenuContent";

import { useAppSelector } from "store/hooks";
import sideMenuService from "services/sideMenu";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import Logo from "assets/images/iban_oplata-logo.svg";

import { Drawer } from "./styles";

const SideMenu: React.FC = () => {
  const sideMenuStorage = sideMenuService();

  const [isCollapsed, setIsCollapsed] = useState(() =>
    sideMenuStorage.getIsCollapsed(),
  );

  const isExpired = useAppSelector(
    (state) => state.subscription.subscription?.isExpired,
  );

  const showPremium = useAppSelector(
    (state) => state.subscription.showSidebarBanner,
  );

  const premiumText = isExpired
    ? "Функціонал доступний лише за активної передплати."
    : "Після закінчення ознайомчого періоду цей функціонал буде недоступний. Оберіть тариф, щоб користуватися ним й надалі.";

  const toggleSideMenu = () => {
    analytics().sendEvent(AnalyticsEvents.side_menu_toggle);
    setIsCollapsed((prev) => {
      const nextValue = !prev;
      if (nextValue) {
        sideMenuStorage.collapseSideMenu();
      } else {
        sideMenuStorage.openSideMenu();
      }
      return nextValue;
    });
  };

  return (
    <Drawer open={!isCollapsed} variant="permanent" data-testid="side-menu">
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          {/* HEADING */}
          <Box
            display="flex"
            alignItems="center"
            sx={{
              py: isCollapsed ? 2.5 : 2,
              px: isCollapsed ? 2.25 : 2.5,
            }}
          >
            {!isCollapsed && (
              <>
                <Box component="img" alt="Logo" src={Logo} />
                <Typography
                  noWrap
                  sx={{
                    ml: 1.5,
                    mr: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  IBAN oplata
                </Typography>
              </>
            )}

            <UserMenu isCollapsed={isCollapsed} />
          </Box>

          {/* MAIN NAV */}
          <List sx={{ mx: 1.5, my: 0, p: 0 }}>
            {sideMenuMainItems.map((menuGroup, index) => (
              <Fragment key={`menu-group-${index}`}>
                {menuGroup.map((item) => {
                  // TODO: remove after feature release
                  const showItem = item.featureFlag
                    ? useFeatureFlagEnabled(item.featureFlag)
                    : true;

                  return (
                    showItem && (
                      <SideMenuItem
                        key={item.route}
                        item={item}
                        premiumText={premiumText}
                        showPremium={showPremium}
                        showActive={true}
                        isCollapsed={isCollapsed}
                      />
                    )
                  );
                })}
                {sideMenuMainItems.length - 1 !== index && (
                  <Box sx={{ borderTop: "1px solid #D9D9D9", mb: 1 }} />
                )}
              </Fragment>
            ))}
          </List>
        </Box>
        <Box>
          {/* TRIAL BANNER */}
          {!isCollapsed && <TrialSidebarBanner />}
          {/* BOTTOM NAV */}
          <List sx={{ mx: 1.5, my: 0, p: 0 }}>
            {sideMenuBottomItems.map((item) => (
              <SideMenuItem
                key={item.route}
                item={item}
                showActive={false}
                isCollapsed={isCollapsed}
              />
            ))}
          </List>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Box sx={{ borderTop: "1px solid #D9D9D9", mb: 1 }} />

            <SideMenuToggleButton
              isCollapsed={isCollapsed}
              onCollapseClick={toggleSideMenu}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SideMenu;
